<script setup lang="ts">
import { BREAKPOINTS } from "@/consts";
import toPixel from "@/helpers/shared/toPixel";
import SVG from "@/stories/displays/SVG/SVG.vue";
import TooltipArrow from "@/stories/displays/Tooltip/TooltipArrow.vue";
import type { SVGName } from "@/SVGs";
import { useBreakpoints } from "@vueuse/core";
import { computed, ref, type CSSProperties } from "vue";
import { RouterLink } from "vue-router";

interface Props {
  icon: SVGName;
  to?: string;
  title: string;
  active?: boolean;
  as?: "button" | typeof RouterLink;
}

withDefaults(defineProps<Props>(), {
  as: RouterLink,
});

defineEmits(["click"]);

const containerRef = ref<HTMLElement>();
const isTooltipActive = ref(false);
const tooltipStyles = ref<CSSProperties>({});
const breakpoints = useBreakpoints(BREAKPOINTS);
const isDesktop = breakpoints.greaterOrEqual("desktop");

const iconSize = computed(() => {
  if (isDesktop.value) {
    return 20;
  }

  return 24;
});

const handleHover = () => {
  if (isDesktop.value) {
    return;
  }

  if (containerRef.value) {
    isTooltipActive.value = true;
    const rect = containerRef.value.getBoundingClientRect();
    tooltipStyles.value.left = toPixel(rect.x + rect.width + 12);
    tooltipStyles.value.zIndex = 9999;
  }
};

const handleOver = () => {
  isTooltipActive.value = false;
};
</script>

<template>
  <div ref="containerRef" class="w-full flex justify-center">
    <component
      :is="as"
      :to="to"
      :class="[
        'w-[56px] h-[40px] flex justify-center h100 items-center rounded-lg transition-colors cursor-pointer relative focus-visible-inverse',
        'desktop:w-full desktop:h-auto desktop:gap-3 desktop:justify-start desktop:p-3',
        {
          'bg-darkOcean100 text-darkOcean700 hover:bg-darkOcean200 ': active,
          'text-white hover:bg-darkOcean600': !active,
        },
      ]"
      @mouseover="handleHover"
      @mouseleave="handleOver"
      @click="$emit('click')"
    >
      <SVG :icon="icon" :width="iconSize" :height="iconSize"></SVG>
      <div class="hidden desktop:block">{{ title }}</div>
    </component>
    <div
      v-if="isTooltipActive"
      class="bg-neutral1000 px-4 py-2 fixed text-neutral0 rounded-lg border border-neutral700 b300 animate-fade-in-fast"
      :style="tooltipStyles"
    >
      {{ title }}
      <TooltipArrow variant="black" position="right" />
    </div>
  </div>
</template>
